/**
 * Sidebar Secura Web — même design que SidebarAdminRH,
 * menu limité à Projets et Horaires (Schedule).
 * Carte profil sous le logo (avatar, nom, poste) — non cliquable.
 */
import React, { useContext, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaSignOutAlt, FaTimes, FaChevronRight, FaChevronLeft } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext.jsx';
import { AppContext } from '../../../contexts/AppContext';
import LanguageSelector from '../../ui/LanguageSelector.jsx';
import { assets } from '../../../assets/assets';
import { getPhotoUrl } from '../../../utils/imageUtils';

export default function SidebarEmployeeWeb({ isOpen, onClose, isCollapsed, onToggleCollapse }) {
  const { user, handleLogout } = useAuth();
  const { userData } = useContext(AppContext);
  const { t } = useTranslation();
  const location = useLocation();
  const [avatarFailed, setAvatarFailed] = useState(false);

  const MENU = [
    { id: 'projets', label: t('menu.projects', 'Projets'), icon: assets.projets, path: '/projets' },
    { id: 'schedule', label: t('menu.schedule', 'Schedule'), icon: assets.retards, path: '/horaire' },
  ];

  const displayName = userData?.nom_complet || userData?.name || user?.nom_complet || 'Employé';
  const poste = userData?.poste || userData?.roleName || user?.role?.nom || 'Employé';
  const profileSrc = useMemo(() => {
    const raw = userData?.profileImageUrl || userData?.photo || user?.profileImageUrl || null;
    if (!raw || avatarFailed) return null;
    return getPhotoUrl(raw);
  }, [userData?.profileImageUrl, userData?.photo, user?.profileImageUrl, avatarFailed]);

  return (
    <>
      <div
        className={`fixed inset-y-0 left-0 z-50 shadow-md transform transition-all duration-300 ease-in-out flex flex-col backdrop-blur-sm bg-white/95 border-r border-gray-200 ${
          isCollapsed ? 'lg:w-20 w-80' : 'w-80'
        } ${isOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}
      >
        <div className={`flex items-center h-16 ${isCollapsed ? 'lg:px-2 px-6' : 'px-6'} border-b border-gray-200 bg-white/95`}>
          <div className="flex items-center justify-between w-full">
            {!isCollapsed && (
              <>
                <img src={assets.secura_logo} alt="Secura" className="h-32 w-44 object-contain -ml-6" />
                <button
                  type="button"
                  onClick={onToggleCollapse}
                  className="w-9 h-9 rounded-full bg-[#0026E2] flex items-center justify-center transition-all duration-300 hover:scale-110 hover:shadow-lg hover:bg-[#0019B3] cursor-pointer"
                  title="Fermer le menu"
                >
                  <FaChevronLeft className="text-base text-white" />
                </button>
              </>
            )}
            {isCollapsed && (
              <button
                type="button"
                onClick={onToggleCollapse}
                className="hidden lg:flex w-9 h-9 rounded-full bg-[#0026E2] items-center justify-center transition-all duration-300 hover:scale-110 hover:shadow-lg hover:bg-[#0019B3] cursor-pointer mx-auto"
                title="Ouvrir le menu"
              >
                <FaChevronRight className="text-base text-white" />
              </button>
            )}
            {isCollapsed && (
              <img src={assets.secura_logo} alt="Secura" className="lg:hidden h-32 w-44 object-contain" />
            )}
          </div>
          <button type="button" onClick={onClose} className="lg:hidden ml-auto text-gray-500 hover:text-gray-700">
            <FaTimes />
          </button>
        </div>

        {/* Carte profil — juste sous le logo (affichage seul, pas de navigation) */}
        <div className={`${isCollapsed ? 'lg:px-2 px-4' : 'px-4'} py-3 border-b border-gray-200`}>
          {isCollapsed ? (
            <div
              className="hidden lg:flex mx-auto h-12 w-12 overflow-hidden items-center justify-center"
              title={displayName}
            >
              <img
                src={profileSrc || assets.employes}
                alt=""
                className={`h-full w-full ${profileSrc ? 'object-cover rounded-full' : 'object-contain'}`}
                onError={() => setAvatarFailed(true)}
              />
            </div>
          ) : null}
          <div className={`flex items-center gap-3 ${isCollapsed ? 'lg:hidden' : ''}`}>
            <div className="h-16 w-16 shrink-0 overflow-hidden" title={displayName}>
              <img
                src={profileSrc || assets.employes}
                alt=""
                className={`h-full w-full ${profileSrc ? 'object-cover rounded-full' : 'object-contain'}`}
                onError={() => setAvatarFailed(true)}
              />
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-bold text-[#0B3A4A]">{displayName}</p>
              <p className="truncate text-xs text-gray-500">{poste}</p>
            </div>
          </div>
        </div>

        <nav className={`flex-1 py-6 overflow-y-auto ${isCollapsed ? 'lg:px-2 px-4' : 'px-4'}`}>
          <div className="mb-6">
            <h3 className={`text-xs font-semibold uppercase tracking-wider mb-3 px-3 text-gray-500 ${isCollapsed ? 'lg:hidden' : ''}`}>
              {t('common.menu', 'MENU')}
            </h3>
            <div className="space-y-1">
              {MENU.map((item) => {
                const isActive =
                  item.path === '/projets'
                    ? location.pathname.startsWith('/projets') || location.pathname.startsWith('/tasks')
                    : location.pathname.startsWith(item.path);
                return (
                  <Link
                    key={item.id}
                    to={item.path}
                    onClick={onClose}
                    className={`flex items-center ${isCollapsed ? 'lg:justify-center lg:px-2 px-3' : 'px-3'} py-3 rounded-xl text-sm font-medium transition-all duration-300 group ${
                      isActive
                        ? 'bg-gradient-to-r from-blue-50 to-blue-100 text-blue-700 shadow-sm border border-blue-200'
                        : 'text-gray-700 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 hover:text-gray-900 hover:shadow-sm'
                    }`}
                    title={isCollapsed ? item.label : ''}
                  >
                    <img src={item.icon} alt="" className={`${isCollapsed ? 'lg:mr-0 mr-3' : 'mr-3'} h-8 w-8 object-contain`} />
                    <span className={`flex-1 ${isCollapsed ? 'lg:hidden' : ''}`}>{item.label}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        </nav>

        <div className={`${isCollapsed ? 'lg:px-2 px-4' : 'px-4'} py-4 border-t border-gray-200 space-y-3`}>
          <div className={isCollapsed ? 'lg:hidden' : ''}>
            <LanguageSelector dropUp={true} />
          </div>
          <button
            type="button"
            onClick={handleLogout}
            className={`w-full flex items-center ${isCollapsed ? 'lg:justify-center lg:px-2 px-3' : 'px-3'} py-3 rounded-xl text-sm font-medium transition-all duration-200 group text-red-600 hover:bg-red-50 hover:text-red-700`}
            title={isCollapsed ? t('menu.logout', 'Déconnexion') : ''}
          >
            <FaSignOutAlt className={`${isCollapsed ? 'lg:mr-0 mr-3' : 'mr-3'} text-base group-hover:scale-110 transition-transform`} />
            <span className={`flex-1 text-left ${isCollapsed ? 'lg:hidden' : ''}`}>{t('menu.logout', 'Déconnexion')}</span>
          </button>
        </div>
      </div>

      {isOpen && (
        <div className="fixed inset-0 z-40 bg-black bg-opacity-20 backdrop-blur-sm lg:hidden" onClick={onClose} />
      )}
    </>
  );
}
